/**
 * New-module wizard modal. The orchestrator gates rendering on
 * `isNewModuleMenuOpen` and owns all the wizard state (step/name/error/search),
 * the `isCreatingModuleRef` double-submit guard inside `handleCreateModule`, and
 * the focus effect that targets `newModuleNameInputRef`. This component is
 * purely presentational: it renders the two-step wizard and calls the
 * threaded-in setters/handler.
 */

import type { RefObject } from 'react';
import { NEW_MODULE_CORE_CATALOG, NEW_MODULE_INDIVIDUAL_CATALOG, NEW_MODULE_TEAM_CATALOG } from '../constants';
import type { SidebarModuleItem } from '../types';

type NewModuleStep = { type: string; label: string; emoji: string; nativeOnly?: boolean } | null;

type CatalogEntry = { emoji: string; label: string; type: string; desc: string; nativeOnly?: boolean };

function ModuleSection({ label, entries, search, onSelect }: {
  label: string;
  entries: CatalogEntry[];
  search: string;
  onSelect: (mod: CatalogEntry) => void;
}) {
  const filtered = entries.filter((m) =>
    !search || m.label.toLowerCase().includes(search.toLowerCase()) || m.desc.toLowerCase().includes(search.toLowerCase())
  );
  if (filtered.length === 0) return null;
  return (
    <div className="new-module-section">
      <div className="new-module-section-label">{label}</div>
      <div className="new-module-grid">
        {filtered.map((mod) => (
          <button key={`${label}:${mod.type}`} className="new-module-card" onClick={() => onSelect(mod)}>
            <span className="new-module-card-emoji">{mod.emoji}</span>
            <span className="new-module-card-label">{mod.label}</span>
            <span className="new-module-card-desc">{mod.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

interface NewModuleModalProps {
  newModuleStep: NewModuleStep;
  setNewModuleStep: (value: NewModuleStep) => void;
  newModuleName: string;
  setNewModuleName: (value: string) => void;
  newModuleNameError: string;
  setNewModuleNameError: (value: string) => void;
  newModuleSearch: string;
  setNewModuleSearch: (value: string) => void;
  isCreatingModule: boolean;
  setIsNewModuleMenuOpen: (value: boolean) => void;
  newModuleNameInputRef: RefObject<HTMLInputElement | null>;
  sidebarModuleItems: SidebarModuleItem[];
  /**
   * Default-deny entitlement filter (ADR 042 §13, Phase 7). A catalog entry whose
   * module `type` is entitlement-hidden for this workspace is dropped from the
   * wizard — a user must not be offered a type they can't create (POST /modules
   * would 403 it). Replaces the legacy `canSeeClients` allowlist (`client_list`
   * is now just one ungranted type among many).
   */
  isModuleTypeHidden: (moduleType: string) => boolean;
  handleCreateModule: (type: string, label: string, nativeOnly?: boolean) => void;
  isTeamWorkspace?: boolean;
}

export function NewModuleModal({
  newModuleStep,
  setNewModuleStep,
  newModuleName,
  setNewModuleName,
  newModuleNameError,
  setNewModuleNameError,
  newModuleSearch,
  setNewModuleSearch,
  isCreatingModule,
  setIsNewModuleMenuOpen,
  newModuleNameInputRef,
  sidebarModuleItems,
  isModuleTypeHidden,
  handleCreateModule,
  isTeamWorkspace,
}: NewModuleModalProps) {
  return (
    <div className="new-module-modal-backdrop" onClick={() => { setIsNewModuleMenuOpen(false); setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); setNewModuleSearch(''); }}>
      <div className="new-module-modal" onClick={(e) => e.stopPropagation()}>
        {!newModuleStep ? (
          <>
            <div className="new-module-modal-header">
              <h2 className="new-module-modal-title">New Module</h2>
              <button className="new-module-modal-close" onClick={() => { setIsNewModuleMenuOpen(false); setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); setNewModuleSearch(''); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div style={{ padding: '0 16px 8px' }}>
              <input
                type="text"
                placeholder="Search modules..."
                value={newModuleSearch}
                onChange={(e) => setNewModuleSearch(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>
            <div className="new-module-modal-body">
              {isTeamWorkspace ? (
                <>
                  <ModuleSection
                    label="Individual"
                    entries={NEW_MODULE_INDIVIDUAL_CATALOG.filter((m) => !isModuleTypeHidden(m.type))}
                    search={newModuleSearch}
                    onSelect={(mod) => { setNewModuleStep({ type: mod.type, label: mod.label, emoji: mod.emoji }); setNewModuleName(mod.label); setNewModuleNameError(''); }}
                  />
                  <ModuleSection
                    label="Team"
                    entries={NEW_MODULE_TEAM_CATALOG.filter((m) => !isModuleTypeHidden(m.type))}
                    search={newModuleSearch}
                    onSelect={(mod) => { setNewModuleStep({ type: mod.type, label: mod.label, emoji: mod.emoji, nativeOnly: mod.nativeOnly }); setNewModuleName(mod.label); setNewModuleNameError(''); }}
                  />
                </>
              ) : (
                <>
                  <ModuleSection
                    label="Core"
                    entries={NEW_MODULE_CORE_CATALOG.filter((m) => !m.teamOnly && !isModuleTypeHidden(m.type))}
                    search={newModuleSearch}
                    onSelect={(mod) => { setNewModuleStep({ type: mod.type, label: mod.label, emoji: mod.emoji }); setNewModuleName(mod.label); setNewModuleNameError(''); }}
                  />
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="new-module-modal-header">
              <h2 className="new-module-modal-title">
                <span className="new-module-card-emoji">{newModuleStep.emoji}</span>
                Name your {newModuleStep.label} module
              </h2>
              <button className="new-module-modal-close" onClick={() => { setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="new-module-modal-body">
              <input
                ref={newModuleNameInputRef}
                className={`new-module-name-input${newModuleNameError ? ' error' : ''}`}
                type="text"
                value={newModuleName}
                disabled={isCreatingModule}
                onChange={(e) => { setNewModuleName(e.target.value); setNewModuleNameError(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isCreatingModule) {
                    const name = newModuleName.trim();
                    if (!name) { setNewModuleNameError('Name is required'); return; }
                    if (sidebarModuleItems.some((item) => (item.displayName || '').toLowerCase() === name.toLowerCase())) { setNewModuleNameError('A module with this name already exists'); return; }
                    handleCreateModule(newModuleStep.type, name, newModuleStep.nativeOnly);
                  }
                  if (e.key === 'Escape') { setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); }
                }}
                placeholder={newModuleStep.label}
              />
              {newModuleNameError && <div className="new-module-name-error">{newModuleNameError}</div>}
              <div className="new-module-name-actions">
                <button className="new-module-btn-back" disabled={isCreatingModule} onClick={() => { setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); }}>Back</button>
                <button className="new-module-btn-create" disabled={isCreatingModule} onClick={() => {
                  const name = newModuleName.trim();
                  if (!name) { setNewModuleNameError('Name is required'); return; }
                  if (sidebarModuleItems.some((item) => (item.displayName || '').toLowerCase() === name.toLowerCase())) { setNewModuleNameError('A module with this name already exists'); return; }
                  handleCreateModule(newModuleStep.type, name, newModuleStep.nativeOnly);
                }}>{isCreatingModule ? 'Creating...' : 'Create'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
